import { useEffect } from "react";

/**
 * On-page SEO for a route inside this single-HTML SPA (no server-side
 * router -- index.html's <head> only ever describes "/" by default). This
 * hook updates the real DOM tags IN PLACE on mount (title, description,
 * canonical, OG, Twitter Card) and restores their exact prior values on
 * unmount, rather than appending duplicates alongside the static ones --
 * a page with two <meta name="description"> tags is invalid and crawlers'
 * handling of the duplicate is undefined. JSON-LD is the one exception:
 * it's added as a SEPARATE script alongside index.html's own WebSite
 * block (Google supports multiple independent JSON-LD blocks per page)
 * and fully removed on unmount, since there's no static per-page
 * equivalent to restore.
 */
export interface SeoTags {
  title: string;
  description: string;
  canonical: string;
  ogType: "website" | "article";
  jsonLd?: Record<string, unknown>;
  // False when a child route owns the tags instead (e.g. the Market Myths
  // grid page must NOT apply its own tags while an EvidenceReader child is
  // mounted and setting more specific ones -- both call this hook
  // unconditionally per React's own rules, so without this flag the two
  // effects would race, and child-before-parent mount ordering means the
  // GENERIC parent tags would win, backwards from what's wanted on a
  // direct deep link to one evidence doc).
  enabled?: boolean;
}

function swapMeta(selector: string, value: string, attr: "content" | "href" = "content"): () => void {
  const el = document.head.querySelector<HTMLElement>(selector);
  if (!el) return () => {};
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return () => {
    if (prev !== null) el.setAttribute(attr, prev);
  };
}

export function useSeoTags(tags: SeoTags) {
  useEffect(() => {
    if (tags.enabled === false) return;
    const restores: (() => void)[] = [];
    const prevTitle = document.title;
    document.title = tags.title;

    restores.push(swapMeta('meta[name="description"]', tags.description));
    restores.push(swapMeta('link[rel="canonical"]', tags.canonical, "href"));
    restores.push(swapMeta('meta[property="og:type"]', tags.ogType));
    restores.push(swapMeta('meta[property="og:title"]', tags.title));
    restores.push(swapMeta('meta[property="og:description"]', tags.description));
    restores.push(swapMeta('meta[property="og:url"]', tags.canonical));
    restores.push(swapMeta('meta[name="twitter:title"]', tags.title));
    restores.push(swapMeta('meta[name="twitter:description"]', tags.description));

    let jsonLdEl: HTMLScriptElement | null = null;
    if (tags.jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.text = JSON.stringify(tags.jsonLd);
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = prevTitle;
      restores.forEach((r) => r());
      if (jsonLdEl) document.head.removeChild(jsonLdEl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags.enabled, tags.title, tags.description, tags.canonical, tags.ogType, JSON.stringify(tags.jsonLd)]);
}
